import { defineStore } from "pinia";
import { computed, reactive, toRefs } from "vue";
import {
  useBugReporter,
  useBusinessStore,
  useDirectBackend,
  usePinia,
} from "#imports";

export type BrandingMediaName =
  | "marketing_cover_desktop"
  | "marketing_cover_mobile"
  | "loyalty_marketing_banner"
  | "primary_logo"
  | "lp_image_1"
  | "lp_image_2"
  | "lp_image_3"
  | "lp_image_4";

// Define the state interface
type IState = {
  isLoaded: boolean;
  medias: { name: BrandingMediaName; url: string }[];
};

// Helper function to create the initial state
function createInitialState(): IState {
  return {
    isLoaded: false,
    medias: [],
  };
}

export const useBrandingStore = defineStore("branding", () => {
  // Create a reactive state object using the initial state
  const state = reactive<IState>(createInitialState());

  const { businessRepo } = useDirectBackend();
  const { report } = useBugReporter();

  const pinia = usePinia();
  const businessStore = useBusinessStore(pinia);

  const businessPrimaryLogo = computed(() => {
    return getMediaUrl("primary_logo") ?? businessStore.logo;
  });

  // Helper function to reset the state to its initial values
  function reset() {
    Object.assign(state, createInitialState());
  }

  // Helper function to patch the state with a partial object
  function patch(patch: Partial<IState>) {
    Object.assign(state, patch);
  }

  function setMedias(
    data: { name: string | BrandingMediaName; url: string }[],
  ) {
    patch({
      medias: data as IState["medias"],
    });
  }

  async function loadMedias() {
    try {
      const res = await businessRepo.getMedias({
        businessId: businessStore.id,
      });

      setMedias(res.data);
    } catch (error) {
      report(error);
    }
  }

  function getMediaUrl(key: BrandingMediaName) {
    return state.medias.find((media) => media.name === key)?.url;
  }

  return {
    ...toRefs(state),
    state: computed(() => state),
    patch,
    reset,
    loadMedias,
    setMedias,
    getMediaUrl,
    businessPrimaryLogo,
  };
});
